import { Box } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NEUTRAL, SURFACE } from "@/theme/tokens";
import { FilterPillButton } from "@/ui/Filters/FilterPillButton";
import { SystemSearchField } from "@/ui/Inputs/SystemSearchField";
import type { GapsDayFilter } from "./gapsDayModel";

type Props = {
	searchTerm: string;
	onSearchChange: (next: string) => void;
	filter: GapsDayFilter;
	isNarrowed: boolean;
	onOpenFilter: () => void;
	/** Shows the scope but will not open it — for a tab the scope does not apply to. */
	isFilterDisabled: boolean;
	/** Keeps the term but takes no input — for a tab with no names to search (the Posted tab's open shifts). */
	isSearchDisabled: boolean;
};

/**
 * Search, and the filter behind it, in one pinned row.
 *
 * The filter button names the scope it is holding — `Nurse · 3 units` — rather than just "Filter", so a
 * supervisor can tell at a glance that the numbers above are theirs and not the building's, without
 * opening anything. Everything shows everything: the plain label is the unfiltered state.
 */
export const GapsSearchRow: FC<Props> = ({ searchTerm, onSearchChange, filter, isNarrowed, onOpenFilter, isFilterDisabled, isSearchDisabled }) => {
	const { t } = useTranslation();

	const filterLabel = useMemo<string>(() => {
		const parts: string[] = [];
		if (filter.jobLabels !== undefined) {
			parts.push(filter.jobLabels.size === 1 ? Array.from(filter.jobLabels)[0] : t("gaps.filter.summaryJobs", { count: filter.jobLabels.size }));
		}
		if (filter.locationNames !== undefined) parts.push(t("gaps.filter.summaryUnits", { count: filter.locationNames.size }));
		if (filter.timeLabels !== undefined) parts.push(t("gaps.filter.summaryShifts", { count: filter.timeLabels.size }));
		return parts.length > 0 ? parts.join(" · ") : t("gaps.filter.title");
	}, [filter, t]);

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.875, px: 1.375, py: 0.875, bgcolor: SURFACE, borderTop: `1px solid ${NEUTRAL[150]}` }}>
			{/* The artifact's dense search row (`.sfield`, ~34px): pinned chrome that has to leave the list its room. */}
			<SystemSearchField
				variant="compact"
				value={searchTerm}
				onChange={onSearchChange}
				placeholder={t("search.staff")}
				name="gaps-staff-search"
				disabled={isSearchDisabled}
				fullWidth
				grow
				dataTestId="gaps-search-input"
			/>
			{/* Names the scope it holds, so the label is also its accessible name; a long scope ellipsizes at 45%. */}
			<FilterPillButton
				label={filterLabel}
				isActive={isNarrowed}
				onClick={onOpenFilter}
				disabled={isFilterDisabled}
				// Pinned chrome on a phone: the pill reaches 44px on touch.
				touchTarget
				dataTestId="gaps-filter-open"
				sx={{ maxWidth: "45%" }}
			/>
		</Box>
	);
};
